"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarCheck, Truck, Zap, RotateCcw, ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const steps = [{
  stepNumber: "01",
  title: "Submit Request",
  description: "Provide your contact information, requested rental dates, delivery location, trailer size, intended use, temperature requirements, available site power, and any optional-equipment needs.",
  detail: "Availability is confirmed with your quote.",
  icon: CalendarCheck
}, {
  stepNumber: "02",
  title: "Review Details",
  description: "Fridge Fleet reviews the requested trailer size, delivery location, intended use, temperature requirements, site access, and available power. Generator and shelving requests for eligible smaller trailers and all 53-foot reefer requirements are reviewed separately.",
  detail: "Pricing is based on the information submitted",
  icon: Truck
}, {
  stepNumber: "03",
  title: "Confirm Quote and Scheduling",
  description: "We confirm trailer availability, quote-based pricing, rental terms, delivery requirements, and delivery and pickup scheduling.",
  detail: "Availability and scheduling are confirmed with each quote",
  icon: Zap
}, {
  stepNumber: "04",
  title: "Delivery and Pickup",
  description: "Once confirmed, delivery, positioning, setup, and pickup arrangements are scheduled based on trailer size, delivery location, and site access. Requirements for the 53-foot reefer are confirmed individually.",
  detail: "Delivery and pickup scheduling are confirmed with each quote.",
  icon: RotateCcw
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="how-delivery-works" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold tracking-wider uppercase mb-4 border border-border">
            <span className="size-2 rounded-full bg-primary" />
            Standard operating procedure
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">Our Delivery and Setup Process</h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">Follow four steps from requesting a quote through confirmed delivery and pickup. Requirements vary by trailer size, delivery location, site access, and rental dates.</p>
        </div>

        {/* Sequential Step Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => {
          const IconComponent = step.icon;
          return <motion.div key={step.stepNumber} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full bg-card text-card-foreground border border-border rounded-lg shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between p-6">
                  <div>
                    {/* Step badge and icon */}
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                      <span className="text-xs font-bold font-mono tracking-wider text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                        STEP {step.stepNumber}
                      </span>
                      <div className="size-10 rounded-md bg-muted flex items-center justify-center text-foreground">
                        <IconComponent className="size-5 text-primary" />
                      </div>
                    </div>

                    {/* Step Title & Primary Description */}
                    <CardHeader className="p-0 mb-3">
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {step.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0">
                      <CardDescription className="text-sm font-sans text-foreground leading-relaxed">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  {/* Operational Detail Footnote */}
                  <div className="mt-6 pt-4 border-t border-border/70 text-xs text-muted-foreground font-sans">
                    {step.detail}
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Operational Assurance & Dual Direct Dispatch Cluster */}
        <div className="mt-12 md:mt-16 rounded-lg bg-card border border-border p-6 md:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 shadow-xs">
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="p-2.5 rounded-md bg-muted shrink-0 text-foreground">
              <ShieldCheck className="size-6 text-primary" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold font-default text-card-foreground">Quote and Scheduling</h3>
              <p className="mt-1 text-sm text-muted-foreground font-sans">
                Coverage, trailer availability, delivery requirements, rental terms, and scheduling are confirmed with each quote.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <Button asChild className="w-full sm:w-auto font-medium">
              <Link to="/ContactRequestAQuotePage">
                Request a Quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <a href="tel:2135218821" className="text-xs sm:text-sm font-mono text-muted-foreground font-medium px-2 py-1.5">
              213-521-8821
            </a>
          </div>
        </div>
      </div>
    </section>;
}